import React, {useState, useMemo, useEffect, useCallback} from 'react';
import CopyConfirmModal from '../../../Components/Common/CopyConfirmModal';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from 'reactstrap';
import {Link, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {getInvoicesList, retryInvoice} from '../../../slices/invoicing/thunk';
import {getInvoiceFile} from '../../../api/api';
import TableContainer from '../../../Components/Common/TableContainer';
import PdfViewerModal from '../../../Components/PdfViewerModal/PdfViewerModal';
import {toast} from 'react-toastify';
import {debounce} from 'lodash';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDecimal } from '../../../utils/numberUtils';

const BillingList = () => {
    document.title = "Lista de Facturas | Vuelo";

    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const invoices = useSelector((state: any) => state.Invoicing.invoices);
    const meta = useSelector((state: any) => state.Invoicing.meta);
    const isLoading = useSelector((state: any) => state.Invoicing.isLoading);
    const error = useSelector((state: any) => state.Invoicing.error);

    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [sriMessage, setSriMessage] = useState<any>(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [search, setSearch] = useState('');
    const [isPageChanging, setIsPageChanging] = useState(false);
    const [retryingIds, setRetryingIds] = useState<string[]>([]);
    const [optimisticEnqueuedIds, setOptimisticEnqueuedIds] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    const debouncedSearch = useMemo(() =>
            debounce((value: string) => {
                setPage(1);
                setSearch(value);
            }, 500),
        []
    );

    const statusOptions = [
        {value: '', label: 'Todos los estados'},
        {value: 'AUTHORIZED', label: 'AUTORIZADO'},
        {value: 'UNAUTHORIZED', label: 'NO AUTORIZADO'},
        {value: 'REJECTED', label: 'DEVUELTA'},
        {value: 'ACCEPTED', label: 'RECIBIDA'},
        {value: 'INPROGRESS', label: 'EN PROCESO'},
        {value: 'DRAFT', label: 'BORRADOR'},
        {value: 'ERROR', label: 'ERROR'}
    ];

    const statusFilterComponent = (
        <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
            }}
        >
            {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );

    const dateFilterComponent = (
        <input
            type="date"
            className="form-control"
            value={dateFilter}
            onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
            }}
            placeholder="Fecha Emisión"
        />
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        setIsPageChanging(true);
        dispatch(getInvoicesList({page, limit, search, status: statusFilter, issueDate: dateFilter}))
            .finally(() => setIsPageChanging(false));
    }, [dispatch, page, limit, search, statusFilter, dateFilter]);

    useEffect(() => {
        const pendingStatuses = ['DRAFT', 'EN_PROCESO'];
        const hasPending = invoices?.some((inv: any) =>
            pendingStatuses.includes(inv.status)
        );

        let interval: NodeJS.Timeout;

        if (hasPending && !isPageChanging) {
            interval = setInterval(() => {
                dispatch(getInvoicesList({page, limit, search}));
            }, 5000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [invoices, dispatch, page, limit, search, isPageChanging]);

    useEffect(() => {
        if (error) {
            toast.error(error || "Ocurrió un error al cargar las facturas.");
        }
    }, [error]);

    const handleDownload = useCallback(async (
        extension: 'pdf' | 'xml',
        accessKey: string,
    ) => {
        try {
            const response: any = await getInvoiceFile(accessKey, extension);

            {
                const fileInstance = document.createElement('a');
                const href = response?.url;
                if (!href) {
                    toast.error('URL del archivo no disponible');
                    return;
                }
                fileInstance.href = href;
                fileInstance.target = '_blank';
                document.body.appendChild(fileInstance);
                fileInstance.click();
                document.body.removeChild(fileInstance);
            }
        } catch (err) {
            toast.error(`No se pudo descargar el archivo ${extension.toUpperCase()}.`);
        }
    }, []);

    const handleViewPdf = useCallback(async (accessKey: string) => {
        try {
            const response: any = await getInvoiceFile(accessKey, 'pdf');
            setPdfUrl(response.url);
            setPdfModalOpen(true);
        } catch (err) {
            toast.error("No se pudo cargar el archivo PDF.");
        }
    }, []);

    const handleViewError = useCallback((messages: any[] = []) => {
        setSriMessage(messages);
        setErrorModalOpen(true);
    }, []);

    const handleRetry = useCallback(async (rowData: { id: string; progressJobId: string; progressStatus: string; status: string; sriMessage: any }) => {
        const { id, progressJobId, progressStatus, status, sriMessage } = rowData;

        // Determinar el progressStatus válido para la cola correspondiente
        let validProgressStatus = progressStatus;
        const validStatuses = ['ENQUEUED', 'CREATED_XML', 'SIGNED', 'SENT_SRI'];
        
        if (!validStatuses.includes(progressStatus)) {
            if (status === 'REJECTED' || status === 'DEVUELTA' || status === 'UNAUTHORIZED' || (Array.isArray(sriMessage) && sriMessage.length > 0)) {
                validProgressStatus = 'SENT_SRI';
            } else {
                validProgressStatus = 'ENQUEUED';
            }
        }

        setRetryingIds(prev => [...prev, id]);

        try {
            const response: any = await dispatch(retryInvoice({ progressJobId, progressStatus: validProgressStatus })).unwrap();

            if (response?.success) {
                setOptimisticEnqueuedIds(prev => [...prev, id]);
            }

            setTimeout(() => {
                dispatch(getInvoicesList({page, limit, search}));
                setOptimisticEnqueuedIds(prev => prev.filter(enqId => enqId !== id));
                setRetryingIds(prev => prev.filter(retryId => retryId !== id));
            }, 5000);

        } catch (error) {
            console.error(error);
            setRetryingIds(prev => prev.filter(retryId => retryId !== id));
        }
    }, [dispatch, page, limit, search]);

    const copyToClipboard = (text: string) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text)
                .then(() => toast.success("Clave de acceso copiada con éxito"))
                .catch(() => toast.error("No se pudo copiar la clave de acceso"));
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "fixed";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success("Clave de acceso copiada con éxito");
            } catch (err) {
                toast.error("No se pudo copiar la clave de acceso");
            }
            document.body.removeChild(textArea);
        }
    };

    const [copyConfirmOpen, setCopyConfirmOpen] = useState<boolean>(false);
    const [invoiceToCopy, setInvoiceToCopy] = useState<any>(null);

    const handleCopyInvoice = useCallback((invoice: any) => {
        try {
            const invoiceData = {
                invoiceInfo: invoice.jsonContent?.infoFactura || {},
                details: invoice.jsonContent?.detalles || [],
                additionalInfo: invoice.jsonContent?.infoAdicional || [],
                payments: invoice.jsonContent?.infoFactura?.pagos || [],
                taxInfo: invoice.jsonContent?.infoTributaria || {}
            };

            sessionStorage.setItem('copiedInvoiceData', JSON.stringify(invoiceData));

            navigate('/billing');
        } catch (error) {
            console.error('Error al copiar factura:', error);
            toast.error("No se pudo copiar la factura");
        }
    }, [navigate]);

    const onRequestCopy = useCallback((invoice: any) => {
        setInvoiceToCopy(invoice);
        setCopyConfirmOpen(true);
    }, []);

    const columns = useMemo(
        () => [
            {
                header: 'Secuencial',
                accessorKey: 'sriSequential',
                enableColumnFilter: false,
            },
            {
                header: 'Clave de Acceso',
                accessorFn: (row: any) => row?.accessKey ?? row?.taxInfo?.accessKey ?? '',
                enableColumnFilter: false,
                cell: (cell: any) => (
                    <div className="d-flex align-items-center">
                        <span className="text-truncate" style={{maxWidth: '150px'}} title={cell.getValue()}>
                            {cell.getValue()}
                        </span>
                        <i
                            className="ri-file-copy-line ms-2 text-muted"
                            onClick={() => copyToClipboard(cell.getValue())}
                            title="Copiar Clave de Acceso"
                            style={{cursor: 'pointer'}}
                        ></i>
                    </div>
                ),
            },
            {
                header: 'Cliente',
                accessorFn: (row: any) => row.jsonContent?.infoFactura?.razonSocialComprador || 'N/A',
                enableColumnFilter: false,
            },
            {
                header: 'Identificación',
                accessorFn: (row: any) => row.jsonContent?.infoFactura?.identificacionComprador || 'N/A',
                enableColumnFilter: false,
            },
            {
                header: 'Fecha Emisión',
                accessorKey: 'issueDate',
                enableColumnFilter: false,
            },
            {
                header: 'Total',
                accessorKey: 'totalAmount',
                enableColumnFilter: false,
                cell: (cell: any) => `$${formatDecimal(cell.getValue())}`,
            },
            {
                header: 'Estado',
                accessorKey: 'status',
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const isRetrying = retryingIds.includes(row.id);
                    const isOptimisticEnqueued = optimisticEnqueuedIds.includes(row.id);

                    if (isRetrying) {
                        return (
                            <span className="badge bg-info d-inline-flex align-items-center">
                              <Spinner size="sm" className="me-1"/>
                              REINTENTANDO...
                            </span>
                        );
                    }

                    const value = isOptimisticEnqueued ? 'ENQUEUED' : cell.getValue();
                    let badgeClass;
                    let label;
                    switch (value) {
                        case 'AUTHORIZED':
                            badgeClass = 'badge bg-success';
                            label = 'AUTORIZADO';
                            break;

                        case 'UNAUTHORIZED':
                            badgeClass = 'badge bg-danger';
                            label = 'NO AUTORIZADO';
                            break;

                        case 'REJECTED':
                            badgeClass = 'badge bg-warning';
                            label = 'DEVUELTA';
                            break;

                        case 'ACCEPTED':
                            badgeClass = 'badge bg-info';
                            label = 'RECIBIDA';
                            break;

                        case 'INPROGRESS':
                            badgeClass = 'badge bg-info';
                            label = 'EN PROCESO';
                            break;

                        default:
                            badgeClass = 'badge bg-secondary';
                            label = value;
                    }
                    return <span className={badgeClass}>{label}</span>;
                },
            },
            {
                header: 'Acciones',
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const status = row.status;
                    const isRetrying = retryingIds.includes(row.id);
                    const isOptimisticEnqueued = optimisticEnqueuedIds.includes(row.id);

                    return (
                        <div className="d-flex gap-1 justify-content-center align-items-center">
                            {status === 'AUTHORIZED' ? (
                                <>
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => {
                                            const key = cell?.row?.original?.accessKey ?? cell?.row?.original?.taxInfo?.accessKey;
                                            if (key) {
                                                handleViewPdf(key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta factura.');
                                            }
                                        }}
                                        title="Ver PDF"
                                    >
                                        <i className="ri-eye-fill"></i>
                                    </Button>
                                    <Button
                                        color="warning"
                                        size="sm"
                                        onClick={() => {
                                            const key = cell?.row?.original?.accessKey ?? cell?.row?.original?.taxInfo?.accessKey;
                                            if (key) {
                                                handleDownload('pdf', key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta factura.');
                                            }
                                        }}
                                        title="Descargar PDF"
                                    >
                                        PDF
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => {
                                            const key = cell?.row?.original?.accessKey ?? cell?.row?.original?.taxInfo?.accessKey;
                                            if (key) {
                                                handleDownload('xml', key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta factura.');
                                            }
                                        }}
                                        title="Descargar XML"
                                    >
                                        XML
                                    </Button>
                                </>
                            ) : (
                                <>
                            {status !== 'ACCEPTED' && (
                                (
                                    status === 'DRAFT' &&
                                    ['QUEUE_FAILED_TECHNICAL', 'QUEUE_PENDING'].includes(row.progressStatus) &&
                                    !isOptimisticEnqueued
                                )
                                ||
                                (
                                    status === 'DEVUELTA'
                                )
                                ||
                                (
                                    (() => {
                                        try {
                                            let msgs = row.sriMessage;
                                            if (typeof msgs === 'string') msgs = JSON.parse(msgs);
                                            
                                            let lastAttempt = 0;
                                            if (Array.isArray(msgs) && msgs.length > 0) {
                                                lastAttempt = Number(msgs[msgs.length - 1]?.attempt) || 0;
                                            }

                                            return lastAttempt >= 1;
                                        } catch (e) {
                                            return false;
                                        }
                                    })()
                                )
                            ) && (
                                        <Button
                                            color="warning"
                                            size="sm"
                                            onClick={() => handleRetry(row)}
                                            disabled={isRetrying}
                                            title="Reintentar"
                                        >
                                            <i className="ri-refresh-line"></i>
                                        </Button>
                                    )}
                                    {Array.isArray(cell.row.original.sriMessage) && cell.row.original.sriMessage.length > 0 && (
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() =>
                                                handleViewError(
                                                    cell.row.original.sriMessage?.slice(-1)
                                                )
                                            }
                                            title="Ver Error"
                                        >
                                            <i className="ri-error-warning-fill"></i>
                                        </Button>
                                    )}
                                </>
                            )}
                            <Button
                                color="info"
                                size="sm"
                                onClick={() => onRequestCopy(cell.row.original)}
                                title="Copiar Factura"
                            >
                                <i className="ri-file-copy-2-fill"></i>
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [retryingIds, optimisticEnqueuedIds, handleRetry, onRequestCopy, handleDownload, handleViewError, handleViewPdf]
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Listado de Facturas" pageTitle="Inicio"/>
                <CopyConfirmModal
                    show={copyConfirmOpen}
                    setOpen={setCopyConfirmOpen}
                    itemToCopy={invoiceToCopy}
                    onConfirm={(inv) => {
                        handleCopyInvoice(inv);
                        setInvoiceToCopy(null);
                    }}
                    type="invoice"
                />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h4 className="card-title mb-0">Listado de Facturas</h4>
                                <Link to="/billing">
                                    <Button color="primary">
                                        <i className="ri-add-line align-middle me-1"></i> Nueva Factura
                                    </Button>
                                </Link>
                            </CardHeader>
                            <div className="overflow-x-auto">
                                <CardBody>
                                    <div className="min-w-[1000px]">
                                        <TableContainer
                                            columns={columns}
                                            data={invoices || []}
                                            isGlobalFilter={true}
                                            isCustomPagination={true}
                                            pageCount={meta ? Math.ceil(meta.total / (meta.pageSize || limit)) : 0}
                                            totalCount={meta ? meta.total : 0}
                                            customPageSize={limit}
                                            onPageChange={(newPage: number) => {
                                                setPage(newPage + 1);
                                            }}
                                            onSearch={(searchText: string) => {
                                                if (searchText !== search) {
                                                    debouncedSearch(searchText);
                                                }
                                            }}
                                            loading={isLoading || isPageChanging}
                                            divClass="table-responsive table-card mb-1"
                                            tableClass="align-middle table-nowrap"
                                            theadClass="table-light text-muted"
                                            SearchPlaceholder="Buscar..."
                                            pageIndex={page - 1}
                                            additionalFilters={
                                                <div className="d-flex gap-2">
                                                    {statusFilterComponent}
                                                    {dateFilterComponent}
                                                </div>
                                            }
                                        />
                                    </div>
                                </CardBody>
                            </div>
                        </Card>
                    </Col>
                </Row>
                <PdfViewerModal
                    isOpen={pdfModalOpen}
                    toggle={() => setPdfModalOpen(!pdfModalOpen)}
                    pdfUrl={pdfUrl}
                />
                <Modal isOpen={errorModalOpen} toggle={() => setErrorModalOpen(!errorModalOpen)} centered>
                    <ModalHeader toggle={() => setErrorModalOpen(!errorModalOpen)}>
                        <i className="ri-error-warning-line me-2 text-danger"></i>
                        Error del SRI
                    </ModalHeader>
                    <ModalBody>
                        {sriMessage?.length ? (
                            <div>
                                {sriMessage.map((item: any, index: number) => (
                                    item.messages?.length > 0 && (
                                        item.messages.map((msg: any, msgIndex: number) => (
                                            <div key={`${index}-${msgIndex}`} className="mb-3">
                                                <p className="fw-bold text-danger mb-2">
                                                    {msg.message}
                                                </p>
                                                {msg.additionalInfo && (
                                                    <p className="text-muted mb-0">
                                                        {msg.additionalInfo}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    )
                                ))}
                            </div>
                        ) : (
                            <p>No hay información de error disponible.</p>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button color="primary" onClick={() => setErrorModalOpen(!errorModalOpen)}>
                            Cerrar
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default BillingList;
